/**
 * Photo gallery — justified rows + lightbox.
 *
 * Free-standing: no imports from @theme/*, so a Horizon update that reshapes the
 * Component base class can't break the gallery. Delete this file plus
 * sections/photo-gallery.liquid and blocks/_gallery-image.liquid to remove it.
 *
 * The grid works without JS (CSS flex fallback in the section). This upgrades it
 * to exact justified rows and adds the lightbox. Images are never JS-dependent.
 */

/* ------------------------------------------------------------------ */
/* Justified rows                                                     */
/* ------------------------------------------------------------------ */

/**
 * Lay figures out in rows that each fill the container width at a shared height,
 * keeping every image's aspect ratio (Flickr / Google-Photos style).
 *
 * Greedy: accumulate items into a row until adding one more would make the row
 * shorter than the target height, then freeze the row at the height that makes
 * its items exactly span the width. The final row is capped at the target
 * height so a lone trailing photo isn't blown up to full width.
 */
class JustifiedGrid extends HTMLElement {
  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    this.#layout();

    // Re-justify on width changes only. Observing the element itself would loop
    // (we mutate child heights); observe nothing but the container's inline size.
    this.#observer = new ResizeObserver(this.#onResize);
    this.#observer.observe(this);

    // Late-loading images don't change layout (aspect ratios are known up front
    // from data-ar), so no per-image load listener is needed.
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #lastWidth = 0;

  #onResize = (/** @type {ResizeObserverEntry[]} */ entries) => {
    const width = entries[0]?.contentRect.width ?? this.clientWidth;
    // Height changes we cause must not retrigger a relayout; gate on width.
    if (Math.abs(width - this.#lastWidth) < 1) return;
    this.#lastWidth = width;

    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#layout());
  };

  #layout() {
    const items = /** @type {HTMLElement[]} */ (
      Array.from(this.children).filter((el) => el instanceof HTMLElement && el.dataset.ar)
    );
    if (items.length === 0) return;

    const containerWidth = this.clientWidth;
    if (containerWidth === 0) return;

    this.#lastWidth = containerWidth;

    const gap = Number(this.dataset.gap) || 0;
    const baseHeight = Number(this.dataset.rowHeight) || 260;

    /**
     * Editorial rhythm: alternate a taller row with a shorter one instead of
     * holding one uniform height. A handful of photos then reads as a composed
     * sequence rather than a thin uniform grid — which is what makes a small
     * set look "basic". Row count is unchanged; only the heights vary.
     */
    const rhythm = [1.25, 0.85];
    let rowIndex = 0;
    const targetFor = () => baseHeight * rhythm[rowIndex % rhythm.length];

    // Lead photo spans the full width. Skipped for very small sets, where
    // promoting one photo would leave almost nothing beneath it.
    let queue = items;
    if (this.dataset.lead === 'true' && items.length >= 3) {
      const el = items[0];
      const ar = parseFloat(el.dataset.ar || '1.5') || 1.5;
      // Clamp so a panorama doesn't become a letterbox strip and a portrait
      // doesn't push the rest of the gallery off screen.
      const height = Math.min(containerWidth / ar, baseHeight * 2.2);
      el.style.flex = 'none';
      el.style.width = `${Math.round(containerWidth)}px`;
      el.style.height = `${Math.round(height)}px`;
      el.dataset.galleryLead = '';
      queue = items.slice(1);
    } else {
      delete items[0].dataset.galleryLead;
    }

    /** @type {{ el: HTMLElement, ar: number }[]} */
    let row = [];
    let arSum = 0;

    const flushRow = (/** @type {boolean} */ isLast) => {
      if (row.length === 0) return;

      const gaps = gap * (row.length - 1);
      // Height that makes this row's items span exactly (containerWidth - gaps).
      let height = (containerWidth - gaps) / arSum;

      // Don't upscale a short final row across the whole width.
      const target = targetFor();
      if (isLast && height > target) height = target;

      for (const { el, ar } of row) {
        el.style.flex = 'none';
        el.style.height = `${Math.round(height)}px`;
        el.style.width = `${Math.round(height * ar)}px`;
      }

      row = [];
      arSum = 0;
      rowIndex += 1;
    };

    for (const el of queue) {
      const ar = parseFloat(el.dataset.ar || '1.5') || 1.5;
      row.push({ el, ar });
      arSum += ar;

      // Row's natural height at current width; once it drops to/below this
      // row's target, the row is full enough to freeze.
      const gaps = gap * (row.length - 1);
      const rowHeight = (containerWidth - gaps) / arSum;
      if (rowHeight <= targetFor()) flushRow(false);
    }

    flushRow(true);
    this.dataset.justified = '';
  }
}

/* ------------------------------------------------------------------ */
/* Lightbox                                                           */
/* ------------------------------------------------------------------ */

class GalleryLightbox extends HTMLElement {
  /** @type {{ src: string, alt: string, caption: string }[]} */
  #sources = [];

  /** @type {number} */
  #index = 0;

  /** @type {number} */
  #scrollY = 0;

  connectedCallback() {
    const template = this.querySelector('template[data-gallery-sources]');
    if (template instanceof HTMLTemplateElement) {
      this.#sources = Array.from(template.content.querySelectorAll('[data-src]')).map((el) => ({
        src: el.getAttribute('data-src') || '',
        alt: el.getAttribute('data-alt') || '',
        caption: el.getAttribute('data-caption') || '',
      }));
    }

    this.#dialog = this.querySelector('[data-gallery-dialog]');
    this.#image = this.querySelector('[data-gallery-image]');
    this.#caption = this.querySelector('[data-gallery-caption]');

    // The grid lives in a sibling; find the nearest section and bind its
    // open-buttons to this lightbox by DOM order.
    const root = this.closest('.photo-gallery') ?? document;
    this.#triggers = Array.from(root.querySelectorAll('[data-gallery-open]'));
    this.#triggers.forEach((btn, i) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(i);
      });
    });

    this.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));

    this.#dialog?.addEventListener('keydown', this.#onKeydown);
    // Native dialog backdrop click: the click target is the <dialog> itself.
    this.#dialog?.addEventListener('click', this.#onDialogClick);
    this.#dialog?.addEventListener('close', this.#restoreScroll);
  }

  disconnectedCallback() {
    this.#dialog?.removeEventListener('keydown', this.#onKeydown);
    this.#dialog?.removeEventListener('click', this.#onDialogClick);
    this.#dialog?.removeEventListener('close', this.#restoreScroll);
  }

  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLImageElement | null} */
  #image = null;
  /** @type {HTMLElement | null} */
  #caption = null;
  /** @type {Element[]} */
  #triggers = [];

  /** @param {number} index */
  open(index) {
    if (!this.#dialog || this.#sources.length === 0) return;
    this.#index = index;
    this.#render();

    // Scroll-lock, mirroring assets/dialog.js so the page doesn't jump.
    this.#scrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollY}px`;

    this.#dialog.showModal();
  }

  close() {
    this.#dialog?.close();
  }

  /** @param {number} delta */
  step(delta) {
    const count = this.#sources.length;
    if (count === 0) return;
    // Wrap around.
    this.#index = (this.#index + delta + count) % count;
    this.#render();
  }

  #render() {
    const item = this.#sources[this.#index];
    if (!item || !this.#image) return;
    this.#image.src = item.src;
    this.#image.alt = item.alt;
    if (this.#caption) this.#caption.textContent = item.caption;

    // Hide the arrows when there's only one photo.
    const single = this.#sources.length < 2;
    this.querySelector('[data-gallery-prev]')?.toggleAttribute('hidden', single);
    this.querySelector('[data-gallery-next]')?.toggleAttribute('hidden', single);
  }

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(1);
    }
    // Escape is handled natively by <dialog>.
  };

  #onDialogClick = (/** @type {MouseEvent} */ event) => {
    // A click landing on the dialog element itself (not its children) is a
    // backdrop click.
    if (event.target === this.#dialog) this.close();
  };

  #restoreScroll = () => {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#scrollY, behavior: 'instant' });
  };
}

if (!customElements.get('justified-grid')) {
  customElements.define('justified-grid', JustifiedGrid);
}

if (!customElements.get('gallery-lightbox')) {
  customElements.define('gallery-lightbox', GalleryLightbox);
}
